import { forwardRef, useId, useState } from 'react';
import type {
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  ReactNode,
} from 'react';
import styles from './TextInput.module.css';

export type TextInputState = 'default' | 'success' | 'error' | 'readonly';

export interface TextInputBaseProps {
  /** Label rendered above the input. Use a mono small-caps style per the design system. */
  label?: ReactNode;
  /** Optional small-caps mono label override (e.g. for "REQUIRED" indicator). */
  labelMono?: boolean;
  /** Helper or hint text shown below the input when there's no error. */
  hint?: ReactNode;
  /** Error message — when set, the input renders in the error state and announces via aria-describedby. */
  errorMessage?: ReactNode;
  /** Success message — when set (and no errorMessage), renders the success state. */
  successMessage?: ReactNode;
  /** Optional leading icon (renders inside the input on the left). */
  leadingIcon?: ReactNode;
  /** Optional trailing element (renders inside the input on the right; e.g. show-password toggle). */
  trailingAdornment?: ReactNode;
  /**
   * When true on a `type="password"` input, renders an eye/eye-slash toggle
   * as the trailing adornment. Defaults to false (passwords stay masked).
   * Overrides any caller-supplied `trailingAdornment` for password inputs.
   * Per WCAG, the toggle is a focusable button with an accessible label
   * that updates as visibility changes.
   */
  revealable?: boolean;
  /** Whether the input takes the full width of its container (default true). */
  fullWidth?: boolean;
}

export type TextInputProps = TextInputBaseProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>;

export type TextAreaProps = TextInputBaseProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'>;

/**
 * Single-line text input with optional label + hint + error + success messages.
 * Supports leading icons and trailing adornments (e.g. show-password button).
 *
 * For type=email / type=password / type=tel etc., set the appropriate
 * `autocomplete` attribute on the underlying input — the auth handoff lists
 * the per-screen recommendations (e.g. `username`, `current-password`,
 * `new-password`, `one-time-code`).
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  {
    label,
    labelMono = true,
    hint,
    errorMessage,
    successMessage,
    leadingIcon,
    trailingAdornment,
    revealable = false,
    fullWidth = true,
    className,
    id,
    disabled,
    readOnly,
    type: typeProp,
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = `${inputId}-message`;

  // Password-reveal toggle. Only takes effect on type=password fields.
  const [revealed, setRevealed] = useState(false);
  const isPasswordField = typeProp === 'password';
  const effectiveType = isPasswordField && revealed ? 'text' : typeProp;
  const showReveal = revealable && isPasswordField;
  const effectiveTrailing: ReactNode = showReveal ? (
    <button
      type="button"
      className={styles.revealToggle}
      onClick={() => setRevealed((v) => !v)}
      aria-pressed={revealed}
      aria-label={revealed ? 'Hide password' : 'Show password'}
      tabIndex={0}
    >
      {revealed ? <EyeOffIcon /> : <EyeIcon />}
    </button>
  ) : trailingAdornment;

  const state: TextInputState = errorMessage
    ? 'error'
    : successMessage
      ? 'success'
      : readOnly
        ? 'readonly'
        : 'default';

  const wrapperClasses = [
    styles.wrapper,
    fullWidth ? styles.fullWidth : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const fieldClasses = [
    styles.field,
    styles[`state_${state}`],
    disabled ? styles.disabled : null,
  ]
    .filter(Boolean)
    .join(' ');

  const describedBy = [errorMessage || successMessage || hint ? messageId : null, ariaDescribedBy]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <div className={wrapperClasses}>
      {label && (
        <label
          htmlFor={inputId}
          className={[styles.label, labelMono ? styles.labelMono : null].filter(Boolean).join(' ')}
        >
          {label}
        </label>
      )}

      <div className={fieldClasses}>
        {leadingIcon && (
          <span className={styles.leading} aria-hidden="true">
            {leadingIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          className={styles.input}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={errorMessage ? true : undefined}
          aria-describedby={describedBy}
          {...(effectiveType !== undefined ? { type: effectiveType } : {})}
          {...rest}
        />
        {effectiveTrailing && <span className={styles.trailing}>{effectiveTrailing}</span>}
      </div>

      {errorMessage && (
        <p id={messageId} className={[styles.message, styles.messageError].join(' ')} role="alert">
          {errorMessage}
        </p>
      )}
      {!errorMessage && successMessage && (
        <p id={messageId} className={[styles.message, styles.messageSuccess].join(' ')}>
          {successMessage}
        </p>
      )}
      {!errorMessage && !successMessage && hint && (
        <p id={messageId} className={[styles.message, styles.messageHint].join(' ')}>
          {hint}
        </p>
      )}
    </div>
  );
});

/**
 * Multi-line text area variant. Same prop API as TextInput but renders a
 * <textarea>. Useful for the optional comment in the response-feedback flow
 * or the description fields in admin authoring forms.
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    label,
    labelMono = true,
    hint,
    errorMessage,
    successMessage,
    fullWidth = true,
    className,
    id,
    disabled,
    readOnly,
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = `${inputId}-message`;

  const state: TextInputState = errorMessage
    ? 'error'
    : successMessage
      ? 'success'
      : readOnly
        ? 'readonly'
        : 'default';

  const wrapperClasses = [
    styles.wrapper,
    fullWidth ? styles.fullWidth : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const fieldClasses = [
    styles.field,
    styles.fieldTextarea,
    styles[`state_${state}`],
    disabled ? styles.disabled : null,
  ]
    .filter(Boolean)
    .join(' ');

  const describedBy = [errorMessage || successMessage || hint ? messageId : null, ariaDescribedBy]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    <div className={wrapperClasses}>
      {label && (
        <label
          htmlFor={inputId}
          className={[styles.label, labelMono ? styles.labelMono : null].filter(Boolean).join(' ')}
        >
          {label}
        </label>
      )}

      <div className={fieldClasses}>
        <textarea
          ref={ref}
          id={inputId}
          className={[styles.input, styles.textarea].join(' ')}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={errorMessage ? true : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
      </div>

      {errorMessage && (
        <p id={messageId} className={[styles.message, styles.messageError].join(' ')} role="alert">
          {errorMessage}
        </p>
      )}
      {!errorMessage && successMessage && (
        <p id={messageId} className={[styles.message, styles.messageSuccess].join(' ')}>
          {successMessage}
        </p>
      )}
      {!errorMessage && !successMessage && hint && (
        <p id={messageId} className={[styles.message, styles.messageHint].join(' ')}>
          {hint}
        </p>
      )}
    </div>
  );
});

// ─── Reveal-toggle icons ────────────────────────────────────────────────

// Eye (password masked): clicking REVEALS the password.
function EyeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// Eye with slash (password revealed): clicking MASKS the password.
function EyeOffIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
